import { makeObservable, observable, action, flow, computed } from "mobx";
import { getDepartmentList, getDepartmentById } from "../../services/departments";

class DepartmentsStore {
  currentDepartmentId = null;
  currentDepartment = null;
  departmentList = [];
  meta = null;
  isLoading = false;
  error = false;
  errorMessage = "";
  
  currentPage = 1;
  itemsPerPage = 15;
  searchParams = {};
  sortParams = {
    order_by: ["created_at"],
    order_direction: ["DESC"],
  };

  constructor() {
    makeObservable(this, {
      currentDepartmentId: observable,
      currentDepartment: observable,
      departmentList: observable,
      meta: observable,
      isLoading: observable,
      error: observable,
      errorMessage: observable,
      currentPage: observable,
      itemsPerPage: observable,
      searchParams: observable,
      sortParams: observable,
      
      setCurrentDepartment: action,
      setCurrentDepartmentId: action,
      clearDepartment: action,
      setError: action,
      clearError: action,
      setLoading: action,
      setDepartmentList: action,
      setMeta: action,
      setCurrentPage: action,
      setItemsPerPage: action,
      setSearchParams: action,
      setSortParams: action,
      resetPagination: action,
      
      fetchDepartmentList: flow,
      fetchPage: flow,
      fetchDepartmentById: flow,
      
      hasNextPage: computed,
      hasPreviousPage: computed,
    });
  }

  /**
   * Sets the current department with full data
   * @param {Object} department - Department object with id, name, and other properties
   */
  setCurrentDepartment(department) {
    if (department) {
      this.currentDepartment = department;
      this.currentDepartmentId = department.department_id || department.departmentUuid || department.id || department.uuid;
      this.error = false;
      this.errorMessage = "";
    }
  }

  /**
   * Sets only the department ID (useful when navigating before fetching full data)
   * @param {string} departmentId - Department ID/UUID
   */
  setCurrentDepartmentId(departmentId) {
    this.currentDepartmentId = departmentId;
    // Don't clear currentDepartment if it matches the ID
    if (this.currentDepartment && 
        (this.currentDepartment.department_id !== departmentId &&
         this.currentDepartment.departmentUuid !== departmentId && 
         this.currentDepartment.id !== departmentId && 
         this.currentDepartment.uuid !== departmentId)) {
      this.currentDepartment = null;
    }
  }

  /**
   * Clears the current department
   */
  clearDepartment() {
    this.currentDepartmentId = null;
    this.currentDepartment = null;
    this.error = false;
    this.errorMessage = "";
  }

  /**
   * Sets loading state
   * @param {boolean} loading - Loading state
   */
  setLoading(loading) {
    this.isLoading = loading;
  }

  /**
   * Sets error state with message
   * @param {boolean} hasError - Whether there is an error
   * @param {string} message - Error message (optional)
   */
  setError(hasError, message = "") {
    this.error = hasError;
    this.errorMessage = message || "";
  }

  /**
   * Clears error state
   */
  clearError() {
    this.error = false;
    this.errorMessage = "";
  }

  /**
   * Sets department list
   * @param {Array} departments - Array of department objects
   */
  setDepartmentList(departments) {
    this.departmentList = Array.isArray(departments) ? departments : [];
  }

  /**
   * Sets meta information
   * @param {Object} meta - Meta object with pagination info
   */
  setMeta(meta) {
    this.meta = meta;
  }

  /**
   * Gets the department name (from currentDepartment or fallback)
   * @returns {string} Department name
   */
  getDepartmentName() {
    if (this.currentDepartment) {
      return this.currentDepartment.department_name || this.currentDepartment.name || this.currentDepartment.title || "Department";
    }
    return "Department";
  }

  /**
   * Sets the current page
   * @param {number} page - Page number
   */
  setCurrentPage(page) {
    this.currentPage = page;
  }

  /**
   * Sets items per page and resets to first page
   * @param {number} size - Number of items per page
   */
  setItemsPerPage(size) {
    this.itemsPerPage = size;
    this.currentPage = 1; // Reset to first page when changing items per page
  }

  /**
   * Sets search parameters and resets to first page
   * @param {Object} params - Search parameters
   */
  setSearchParams(params) {
    this.searchParams = params && Object.keys(params).length > 0 ? { search: params } : {};
    this.currentPage = 1;
  }

  /**
   * Sets sort parameters and resets to first page
   * @param {Object} params - Sort parameters (order_by, order_direction)
   */
  setSortParams(params) {
    this.sortParams = params;
    this.currentPage = 1; // Reset to first page when sorting
  }

  /**
   * Resets pagination to initial state
   */
  resetPagination() {
    this.currentPage = 1;
    this.itemsPerPage = 15;
    this.searchParams = {};
    this.sortParams = {
      order_by: ["created_at"],
      order_direction: ["DESC"],
    };
  }

  /**
   * Checks if there's a next page
   * @returns {boolean}
   */
  get hasNextPage() {
    return this.meta?.has_next || false;
  }

  /**
   * Checks if there's a previous page
   * @returns {boolean}
   */
  get hasPreviousPage() {
    return this.currentPage > 1;
  }

  /**
   * Fetches a specific page using current store parameters
   * @param {number} pageOverride - Optional page number to override currentPage
   * @returns {Promise<void>}
   */
  *fetchPage(pageOverride = null) {
    const params = {
      page: pageOverride !== null ? pageOverride : this.currentPage,
      page_size: this.itemsPerPage,
      ...this.sortParams,
      ...this.searchParams,
    };
    
    yield this.fetchDepartmentList(params);
  }

  /**
   * Fetches department list with filters and pagination
   * @param {Object} params - Query parameters
   * @returns {Promise<void>}
   */
  *fetchDepartmentList(params = {}) {
    try {
      this.setLoading(true);
      this.setError(false, "");

      const response = yield getDepartmentList(params);
      
      if (!response) {
        throw new Error('Invalid response from server');
      }

      // Handle both snake_case and camelCase formats
      const statusCode = response.status_code || response.statusCode || 200;
      const msgCode = response.msg_code || response.msgCode || [];
      const responseBody = response.body || [];
      const meta = response.meta || {};

      // Success (2xx)
      if (statusCode >= 200 && statusCode < 300) {
        this.setDepartmentList(Array.isArray(responseBody) ? responseBody : []);
        this.setMeta(meta);
        this.setError(false, "");
        return;
      }

      // Error - extract message from msgCode
      const errorMessage = msgCode.length > 0 
        ? (msgCode[0].description || msgCode[0].code || 'An error occurred while listing departments.')
        : 'An error occurred while listing departments.';

      this.setError(true, errorMessage);
      
      const error = new Error(errorMessage);
      error.response = {
        data: response,
        status: statusCode,
      };
      
      throw error;

    } catch (error) {
      // Handle network errors or API errors
      if (error.response && error.response.data) {
        const responseData = error.response.data;
        const errorMessage = responseData.message || error.message || 'An error occurred while listing departments.';

        this.setError(true, errorMessage);
        
        const apiError = new Error(errorMessage);
        apiError.response = {
          data: responseData,
          status: error.response.status,
        };
        
        throw apiError;
      }

      // Handle other errors (network, etc.)
      const errorMessage = error.message || 'An error occurred while listing departments.';
      this.setError(true, errorMessage);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Fetches a department by ID/UUID
   * @param {string} departmentId - Department ID/UUID
   * @returns {Promise<void>}
   */
  *fetchDepartmentById(departmentId) {
    if (!departmentId) {
      throw new Error("departmentId is required");
    }

    try {
      this.setLoading(true);
      this.setError(false, "");

      const response = yield getDepartmentById(departmentId);
      
      if (!response) {
        throw new Error('Invalid response from server');
      }

      // Handle both snake_case and camelCase formats
      const statusCode = response.status_code || response.statusCode || 200;
      const msgCode = response.msg_code || response.msgCode || [];
      const department = response.body;

      // Success (2xx)
      if (statusCode >= 200 && statusCode < 300 && department) {
        this.setCurrentDepartment(department);
        this.setError(false, "");
        return;
      }

      // Error - extract message from msgCode
      const errorMessage = msgCode.length > 0 
        ? (msgCode[0].description || msgCode[0].code || 'An error occurred while fetching department.')
        : 'An error occurred while fetching department.';

      this.setError(true, errorMessage);
      
      const error = new Error(errorMessage);
      error.response = {
        data: response,
        status: statusCode,
      };
      
      throw error;

    } catch (error) {
      // Handle network errors or API errors
      if (error.response && error.response.data) {
        const responseData = error.response.data;
        const errorMessage = responseData.message || error.message || 'An error occurred while fetching department.';

        this.setError(true, errorMessage);
        
        const apiError = new Error(errorMessage);
        apiError.response = {
          data: responseData,
          status: error.response.status,
        };
        
        throw apiError;
      }

      // Handle other errors (network, etc.)
      const errorMessage = error.message || 'An error occurred while fetching department.';
      this.setError(true, errorMessage);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }
}

export default DepartmentsStore;

